/**
 * Tendoo Action Tracker SDK (Web TypeScript Edition)
 *
 * Fully compliant with Version 2.1 - Production Integration & Parameter Specification (30/10/2026)
 * - Zero-Lag: In-memory Ring Buffer (< 0.02ms UI execution)
 * - 8 Macro Features: f is strictly one of ActionMacroFeature enum (e.g. 'chatbot_v2')
 * - Standard Views: v uses ActionView (e.g. 'chatbot_ai_assistant', 'tax_handbook')
 * - Rage Click Detection: Flagged automatically if >= 3 taps within 1.5s on the same element
 * - Lossless Offline Persistence: localStorage backup ('tendoo_action_tracker_offline_queue')
 * - Batching: Periodic flush every 10s, threshold 30 events, max RAM buffer 100 events
 * - Error Tracking: Structured trackError supporting layers: 'dio' | 'repository' | 'bloc' | 'view' | 'global'
 */

import { generateUUID } from '../utils/uuid';

export const ActionMacroFeature = {
  CHATBOT_V2: 'chatbot_v2',
  T_NOTE: 't-note',
  ORDER_NORMAL: 'order-normal',
  IMPORT_STOCK: 'import_stock',
  FNB: 'fnb',
  AI_CREATION: 'ai_creation',
  AI_SUGGESTION: 'ai_suggestion',
  RE_ORDER: 're_order',
  CORE: 'core',
} as const;
export type ActionMacroFeatureType = typeof ActionMacroFeature[keyof typeof ActionMacroFeature];

export const ActionView = {
  CHATBOT_AI_ASSISTANT: 'chatbot_ai_assistant',
  TAX_HANDBOOK: 'tax_handbook',
  AI_CHAT_BUBBLE: 'ai_chat_bubble',
  T_NOTE: 't_note',
  ORDER_PAYMENT: 'order_payment',
  QR_PAYMENT: 'qr_payment',
  ORDER_NORMAL: 'order_normal',
  IMPORT_STOCK: 'import_stock',
  CAMERA_CAPTURE: 'camera_capture',
  CAMERA_GALLERY: 'camera_gallery',
  AI_EXTRACTION_INFO: 'ai_extraction_info',
  LINK_PRODUCT: 'link_product',
  SEARCH_LINK_PRODUCT: 'search_link_product',
  CREATE_NEW: 'create_new',
  FNB_PROGRESS: 'fnb_progress',
  FNB_EXTRACTION: 'fnb_extraction',
  PRODUCT_CREATION: 'product_creation',
  ORDER_SUGGESTION: 'order_suggestion',
  PURCHASE_HISTORY: 'purchase_history',
} as const;
export type ActionViewType = typeof ActionView[keyof typeof ActionView] | string;

export const ActionType = {
  TAP: 'tap',
  HOLD: 'hold',
  RELEASE: 'release',
  INPUT: 'input',
  SCROLL: 'scroll',
  SWIPE: 'swipe',
  SCAN: 'scan',
  NAV: 'nav',
  VIEW: 'view',
} as const;
export type ActionTypeKey = typeof ActionType[keyof typeof ActionType] | string;

export const ActionElement = {
  BTN_SEND: 'btn_send',
  BTN_MIC: 'btn_mic',
  BTN_PLAY_AUDIO: 'btn_play_audio',
  TAB_REPORT: 'tab_report',
  TAB_TAX: 'tab_tax',
  TAB_GUIDE: 'tab_guide',
  TAB_CSKH: 'tab_cskh',
  TAB_AI: 'tab_ai',
  BTN_SUGGEST: 'btn_suggest',
  BTN_LIKE: 'btn_like',
  BTN_UNLIKE: 'btn_unlike',
  BTN_COPY_MESSAGE: 'btn_copy_message',
  CARD_PRODUCT: 'card_product',
  BTN_CLEAR_HISTORY: 'btn_clear_history',
} as const;

export interface ActionEvent {
  t: number;               // Timestamp in Epoch ms
  s: string;               // Session ID (s_...)
  seq: number;             // Atomic sequence counter
  v: string;               // View name (e.g. 'chatbot_ai_assistant', 'tax_handbook')
  f: string;               // Macro feature (e.g. 'chatbot_v2')
  a: string;               // Action type: 'tap', 'hold', 'release', etc.
  e: string;               // Element identifier: 'btn_send', 'btn_mic', etc.
  d: number;               // Duration in ms
  prev: string;            // Previous action name (element_action)
  err: string | null;      // Error code or null
  x?: Record<string, any> | null; // Extra context (tag, model, flow_id, messageIdStr, is_rage_click, etc.)
}

export interface ActionBatchPayload {
  batch_id: string;
  sent_at: number;
  events_count: number;
  events: ActionEvent[];
}

export interface TrackOptions {
  view?: ActionViewType;
  feature?: ActionMacroFeatureType | string;
  action: ActionTypeKey;
  element: string;
  durationMs?: number;
  errorCode?: string | null;
  extraContext?: Record<string, any>;
}

export interface TrackErrorOptions {
  error: string;
  layer: 'dio' | 'repository' | 'bloc' | 'view' | 'global';
  view?: ActionViewType;
  feature?: ActionMacroFeatureType | string;
  element?: string;
  extraContext?: Record<string, any>;
}

export interface BotContext {
  flow_id?: string;
  messageIdStr?: string;
  intent?: string;
  typeChat?: string;
}

const OFFLINE_STORAGE_KEY = 'tendoo_action_tracker_offline_queue';

const parseJwtPayload = (token: string): Record<string, any> | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

const getAuthFromStorage = (): { userId: string; businessId: string; token: string } => {
  try {
    const token =
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('access_token') : null) ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null) ||
      '';
    if (!token) return { userId: '', businessId: '', token: '' };
    const payload = parseJwtPayload(token);
    const sub = payload?.sub ? String(payload.sub) : '';
    const userId = (sub.split('|')[0] || sub).trim();
    const businessId = payload?.business_id != null ? String(payload.business_id) : '';
    return { userId, businessId, token };
  } catch {
    return { userId: '', businessId: '', token: '' };
  }
};

const getDeviceId = (): string => {
  try {
    const KEY = 'tendoo_action_device_id';
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = 'dev_' + generateUUID().replace(/-/g, '').slice(0, 16);
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return 'dev_web_' + Math.random().toString(36).slice(2, 10);
  }
};

const getDeviceModel = (): string => {
  try {
    const uaData = (navigator as any)?.userAgentData;
    if (uaData?.model) return uaData.model;
    const ua = navigator.userAgent || '';
    if (/mobile/i.test(ua)) return 'Mobile Browser';
    return 'Desktop Browser';
  } catch {
    return 'Web Browser';
  }
};

const getOsVersion = (): string => {
  try {
    const uaData = (navigator as any)?.userAgentData;
    if (uaData?.platform) return uaData.platform;
    return navigator.platform || 'Web';
  } catch {
    return 'Web';
  }
};

export class TendooActionTracker {
  private static instance: TendooActionTracker;

  private _endpoint: string;
  private _platform: string = 'chatbot_web';
  private _appVersion: string = '2.0.49';

  // Session & Sequence State (in RAM)
  private _sessionId: string | null = null;
  private _lastActiveTime: number | null = null;
  private _sequenceCounter: number = 0;
  private _lastActionName: string = 'session_start';

  // Ring Buffer (bounded to 100 items for zero memory leak)
  private _buffer: ActionEvent[] = [];
  private readonly _maxBufferSize: number = 100;
  private readonly _batchThreshold: number = 30;
  private readonly _sessionInactivityMs: number = 30 * 60 * 1000; // 30 minutes

  // Rage Click Tracking (Window: 1.5s, Threshold: >= 3 taps)
  private _clickTimestamps: Map<string, number[]> = new Map();
  private readonly _rageClickWindowMs: number = 1500;
  private readonly _rageClickThreshold: number = 3;

  // Bot Context from previous response
  private _lastBotContext: BotContext = {};
  private _flushTimer: any = null;

  public static getInstance(): TendooActionTracker {
    if (!TendooActionTracker.instance) {
      TendooActionTracker.instance = new TendooActionTracker();
    }
    return TendooActionTracker.instance;
  }

  private constructor() {
    this._endpoint =
      'https://apiv3-dev.tendoo.vn/ai-gateway/api/proxy/job-service/api/v1/actions/ingest';

    this.restoreQueueFromDisk();
    this.initListeners();
  }

  private initListeners(): void {
    if (typeof window === 'undefined') return;

    try {
      this._flushTimer = setInterval(() => {
        this.flush();
      }, 10000);
    } catch {
      // Ignore
    }

    const handleUnload = () => {
      this.flush();
      this.persistQueueToDisk();
    };

    window.addEventListener('beforeunload', handleUnload, { passive: true });
    window.addEventListener('pagehide', handleUnload, { passive: true });

    if (typeof document !== 'undefined') {
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.visibilityState === 'hidden') {
            this.flush();
            this.persistQueueToDisk();
          }
        },
        { passive: true }
      );
    }

    window.addEventListener(
      'online',
      () => {
        this.restoreQueueFromDisk();
        this.flush();
      },
      { passive: true }
    );
  }

  /**
   * Save current buffer to localStorage to ensure lossless persistence during offline/shutdown
   */
  public persistQueueToDisk(): void {
    try {
      if (typeof localStorage === 'undefined' || this._buffer.length === 0) return;
      const json = JSON.stringify(this._buffer.slice(-this._maxBufferSize));
      localStorage.setItem(OFFLINE_STORAGE_KEY, json);
    } catch {
      // Ignore quota errors
    }
  }

  /**
   * Restore offline queue from localStorage on startup or online resume
   */
  public restoreQueueFromDisk(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const raw = localStorage.getItem(OFFLINE_STORAGE_KEY);
      if (!raw) return;
      const parsed: ActionEvent[] = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Prepend restored events to current buffer, keeping max size
        this._buffer = [...parsed, ...this._buffer].slice(-this._maxBufferSize);
      }
      localStorage.removeItem(OFFLINE_STORAGE_KEY);
    } catch {
      // Clear corrupt entry
      try {
        localStorage.removeItem(OFFLINE_STORAGE_KEY);
      } catch {
        // Ignore
      }
    }
  }

  /**
   * Update tracker configuration
   */
  public configure(options: {
    endpoint?: string;
    platform?: string;
    appVersion?: string;
  }): void {
    if (options.endpoint) this._endpoint = options.endpoint;
    if (options.platform) this._platform = options.platform;
    if (options.appVersion) this._appVersion = options.appVersion;
  }

  // Dynamic Feature Resolver Hook (Section 6.3.C)
  public static dynamicFeatureResolver: ((viewOrText: string) => ActionMacroFeatureType | null) | null = null;

  // Timers for stopwatch and screen dwell time (Section 9.4)
  private _timers: Map<string, number> = new Map();
  private _screenTimers: Map<string, number> = new Map();

  /**
   * Start a new session explicitly (e.g. cold start, ca bán hàng mới, etc. - Section 8.1)
   */
  public startNewSession(reason: string = 'manual'): string {
    this._sessionId = 's_' + Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0');
    this._sequenceCounter = 0;
    this._lastActionName = 'session_start';
    this._lastActiveTime = Date.now();
    return this._sessionId;
  }

  public get currentSessionId(): string {
    if (!this._sessionId) {
      this.startNewSession('cold_start');
    }
    return this._sessionId!;
  }

  /**
   * Measure operation duration (Stopwatch - Section 9.4)
   */
  public startTimer(key: string): void {
    this._timers.set(key, Date.now());
  }

  public stopTimer(key: string): number {
    const start = this._timers.get(key);
    this._timers.delete(key);
    if (!start) return 0;
    return Math.max(0, Date.now() - start);
  }

  /**
   * Measure screen dwell time (Section 9.4 & Section 6.6)
   */
  public startScreenTimer(route: string): void {
    this._screenTimers.set(route, Date.now());
  }

  public stopScreenTimer(route: string): void {
    const start = this._screenTimers.get(route);
    this._screenTimers.delete(route);
    if (!start) return;
    const dwellMs = Math.max(0, Date.now() - start);
    this.track({
      view: route,
      action: ActionType.VIEW,
      element: 'screen_dwell',
      durationMs: dwellMs,
    });
  }

  /**
   * Resolve macro feature from view/element text if not provided (Section 6.3.C)
   */
  private _resolveFeatureFromText(text: string): ActionMacroFeatureType {
    if (TendooActionTracker.dynamicFeatureResolver) {
      const hookRes = TendooActionTracker.dynamicFeatureResolver(text);
      if (hookRes) return hookRes;
    }

    const t = text.toLowerCase();
    if (t.includes('chatbot') || t.includes('tax') || t.includes('assistant') || t.includes('tts')) {
      return ActionMacroFeature.CHATBOT_V2;
    }
    if (
      t.includes('quick-order') ||
      t.includes('quick_order') ||
      t.includes('t_note') ||
      t.includes('t-note') ||
      t.includes('fast-order') ||
      t.includes('item-parser') ||
      t.includes('create_order') ||
      t.includes('order_payment') ||
      t.includes('qr_payment')
    ) {
      return ActionMacroFeature.T_NOTE;
    }
    if (t.includes('order_normal') || t.includes('order-normal')) {
      return ActionMacroFeature.ORDER_NORMAL;
    }
    if (t.includes('reorder') || t.includes('re_order') || t.includes('re-order')) {
      return ActionMacroFeature.RE_ORDER;
    }
    if (t.includes('suggestion') || t.includes('catalog') || t.includes('category') || t.includes('similar')) {
      return ActionMacroFeature.AI_SUGGESTION;
    }
    if (t.includes('product_creation') || t.includes('ai_creation') || t.includes('generate-product') || t.includes('media')) {
      return ActionMacroFeature.AI_CREATION;
    }
    if (t.includes('fnb')) {
      return ActionMacroFeature.FNB;
    }
    if (
      t.includes('import_stock') ||
      t.includes('link_product') ||
      t.includes('search_link') ||
      t.includes('create_new') ||
      t.includes('extraction') ||
      t.includes('camera') ||
      t.includes('stock-in') ||
      t.includes('voucher') ||
      t.includes('ocr') ||
      t.includes('/po/') ||
      t.includes('_po') ||
      t.includes('-po')
    ) {
      return ActionMacroFeature.IMPORT_STOCK;
    }
    return ActionMacroFeature.CHATBOT_V2;
  }

  public setLastBotContext(ctx: BotContext): void {
    try {
      this._lastBotContext = {
        ...this._lastBotContext,
        ...ctx,
      };
    } catch {
      // Ignore
    }
  }

  public clearLastBotContext(): void {
    this._lastBotContext = {};
  }

  /**
   * Track user action (< 0.02ms on UI thread)
   */
  public track(options: TrackOptions): void {
    try {
      if (typeof window === 'undefined') return;
      if (localStorage.getItem('disable_action_log') === 'true') return;

      const now = Date.now();

      // 1. Session Lifecycle (30 min inactivity timeout)
      if (
        !this._sessionId ||
        !this._lastActiveTime ||
        now - this._lastActiveTime >= this._sessionInactivityMs
      ) {
        this.startNewSession('timeout_inactivity');
      }
      this._lastActiveTime = now;

      // 2. Atomic sequence progression
      const seq = ++this._sequenceCounter;
      const prev = this._lastActionName;
      this._lastActionName = `${options.element}_${options.action}`;

      // 3. Rage Click Detection: >= 3 taps within 1.5s window
      let isRageClick = false;
      if (options.action === ActionType.TAP) {
        const timestamps = this._clickTimestamps.get(options.element) || [];
        const recent = timestamps.filter((t) => now - t <= this._rageClickWindowMs);
        recent.push(now);
        this._clickTimestamps.set(options.element, recent);
        if (recent.length >= this._rageClickThreshold) {
          isRageClick = true;
        }
      }

      // 4. Merge extraContext
      const mergedExtra: Record<string, any> = {
        ...(this._lastBotContext.messageIdStr || this._lastBotContext.flow_id ? this._lastBotContext : {}),
        ...(options.extraContext || {}),
        ...(isRageClick ? { is_rage_click: true } : {}),
      };

      // 5. Automatic Feature Resolution (Section 6.3.C)
      const resolvedFeature =
        options.feature ||
        this._resolveFeatureFromText(`${options.view || ''} ${options.element || ''}`);

      // 6. Build compact event (Section 6.1)
      const event: ActionEvent = {
        t: now,
        s: this._sessionId!,
        seq,
        v: options.view || ActionView.CHATBOT_AI_ASSISTANT,
        f: resolvedFeature,
        a: options.action,
        e: options.element,
        d: options.durationMs || 0,
        prev,
        err: options.errorCode || null,
        x: Object.keys(mergedExtra).length > 0 ? mergedExtra : null,
      };

      // 7. Ring buffer append (FIFO drop oldest)
      this._buffer.push(event);
      if (this._buffer.length > this._maxBufferSize) {
        this._buffer.shift();
      }

      // Flush if threshold reached
      if (this._buffer.length >= this._batchThreshold) {
        this.flush();
      }
    } catch {
      // Non-blocking safe catch
    }
  }

  /**
   * Specialized error tracking adhering to Section 8.3.B
   */
  public trackError(options: TrackErrorOptions): void {
    try {
      this.track({
        view: options.view || ActionView.CHATBOT_AI_ASSISTANT,
        feature: options.feature || ActionMacroFeature.CHATBOT_V2,
        action: ActionType.TAP,
        element: options.element || 'error_catch',
        errorCode: options.error,
        extraContext: {
          layer: options.layer,
          error_code: options.error,
          ...(options.extraContext || {}),
        },
      });
    } catch {
      // Safe fail
    }
  }

  /**
   * Flush pending events from RAM to Gateway
   */
  public flush(): void {
    try {
      if (this._buffer.length === 0) return;

      const eventsToSend = [...this._buffer];
      this._buffer = [];

      const { userId, businessId, token } = getAuthFromStorage();

      // Format payload: Wrap envelope if endpoint is /batch, otherwise send JSON Array
      const isBatchEnvelopeEndpoint = this._endpoint.includes('/batch');
      const bodyPayload = isBatchEnvelopeEndpoint
        ? {
            batch_id: `btc_${Date.now()}`,
            sent_at: Date.now(),
            events_count: eventsToSend.length,
            events: eventsToSend,
          }
        : eventsToSend;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json; charset=utf-8',
        'x-platform': this._platform,
        'x-device-id': getDeviceId(),
        'x-device-model': getDeviceModel(),
        'x-os-version': getOsVersion(),
        'app-version': this._appVersion,
      };

      if (userId) headers['x-user-id'] = userId;
      if (businessId) headers['x-business-id'] = businessId;
      if (token) {
        headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      }

      fetch(this._endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(bodyPayload),
        keepalive: true,
      })
        .then((res) => {
          if (!res.ok && res.status >= 500) {
            // Server 5xx error: Restore failed batch and save to disk
            this._restoreFailedBatch(eventsToSend);
          }
        })
        .catch((err) => {
          // Network offline / failure: Restore failed batch and save to disk
          this._restoreFailedBatch(eventsToSend);
          if (
            process.env.NODE_ENV === 'development' &&
            typeof window !== 'undefined' &&
            (window as any).__ACTION_LOG_DEBUG__
          ) {
            console.debug('[TendooActionTracker] Flush offline / error, saved to disk:', err);
          }
        });
    } catch {
      // Safe fail
    }
  }

  private _restoreFailedBatch(failedEvents: ActionEvent[]): void {
    try {
      this._buffer = [...failedEvents, ...this._buffer].slice(-this._maxBufferSize);
      this.persistQueueToDisk();
    } catch {
      // Ignore
    }
  }

  public destroy(): void {
    if (this._flushTimer) {
      clearInterval(this._flushTimer);
      this._flushTimer = null;
    }
    this.flush();
    this.persistQueueToDisk();
  }
}

export const actionTracker = TendooActionTracker.getInstance();
